import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BasicInfoSection, AssignmentSection, ScheduleSection, TemplateSection, LinksSection } from "./OfficeTaskFormSections";
import { toast } from "sonner";

const EMPTY = {
  title: "", description: "", department: "Sales", category: "general", task_type: "manual",
  assigned_to: "", reporting_manager: "", priority: "normal", due_date: "", due_time: "",
  project_id: "", customer_code: "", bill_no: "", visibility: "private", confidential: false,
  estimated_effort_mins: "", ack_required: false, blocked_by: "", template_id: "",
  workspace: "office", start_date: "", expected_output: "", acceptance_criteria: "", review_required: true,
  deadline_change_reason: "", checklist: "", collaborators: [],
};

export function OfficeTaskForm({ open, onOpenChange, task, users, projects, meta, onSaved, fixedProject, prefill }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [dupes, setDupes] = useState([]);
  const dupeKey = [f.title, f.department, f.category, f.assigned_to, f.customer_code].join("|");
  const [templates, setTemplates] = useState([]);
  const editing = !!task;

  useEffect(() => {
    if (!open) return;
    if (task) {
      setF({
        ...EMPTY,
        title: task.title || "", description: task.description || "",
        department: task.department || "Sales", category: task.category || "general",
        task_type: task.task_type || "manual",
        assigned_to: task.assigned_to?.user_id || "", reporting_manager: task.reporting_manager?.user_id || "",
        priority: task.priority || "normal", due_date: task.due_date || "", due_time: task.due_time || "",
        project_id: task.project_id || fixedProject || "",
        customer_code: task.links?.customer_code || "", bill_no: task.links?.bill_no || "",
        visibility: task.visibility || "private", confidential: !!task.confidential,
        estimated_effort_mins: task.estimated_effort_mins ? String(task.estimated_effort_mins) : "", ack_required: !!task.ack_required,
        workspace: task.workspace || "office", start_date: task.start_date || "", expected_output: task.expected_output || "",
        acceptance_criteria: task.acceptance_criteria || "", review_required: task.review_required !== false,
      });
    } else {
      const p = prefill || {};
      const dept = p.department || "Sales and Business Development";
      setF({ ...EMPTY, project_id: fixedProject || "", title: p.title || "", description: p.description || "", department: dept,
        workspace: (meta?.workspaces?.factory || []).includes(dept) ? "factory" : "office", task_type: "standard",
        priority: p.priority || "normal", due_date: p.due_date || "", expected_output: p.expected_output || "",
        acceptance_criteria: p.acceptance_criteria || "", assigned_to: p.assigned_to || "", checklist: (p.checklist || []).join("\n") });
    }
  }, [open, task, fixedProject, prefill, meta]);

  useEffect(() => {
    if (!open || task) return;
    api.get("/office/templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, [open, task]);

  useEffect(() => {
    if (!open || task || !f.title || f.title.trim().length < 4) {
      setDupes([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const r = await api.post("/office/tasks/check-duplicates", {
          title: f.title, department: f.department, category: f.category,
          assigned_to: f.assigned_to || undefined, customer_code: f.customer_code || undefined,
        });
        setDupes(r.data);
      } catch (err) { console.warn("duplicate check skipped", err?.message); }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dupeKey, open, task]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.template_id && !f.title.trim()) return toast.error("Title is required");
    if (!editing && !f.assigned_to) return toast.error("Accountable owner is required");
    if (!editing && !f.due_date && f.task_type !== "project") return toast.error("Deadline is required");
    if (editing && (f.due_date || "") !== (task.due_date || "") && !f.deadline_change_reason.trim()) return toast.error("Reason required for changing the deadline");
    setBusy(true);
    try {
      if (editing) {
        const payload = {};
        for (const k of ["title", "description", "department", "category", "priority", "due_date", "due_time", "start_date", "expected_output", "acceptance_criteria"]) {
          if ((f[k] || "") !== (task[k] ?? "")) payload[k] = f[k] || null;
        }
        if (payload.due_date !== undefined) payload.deadline_change_reason = f.deadline_change_reason;
        if (f.review_required !== (task.review_required !== false)) payload.review_required = f.review_required;
        if (f.assigned_to !== task.assigned_to?.user_id) payload.assigned_to = f.assigned_to;
        if ((f.reporting_manager || "") !== (task.reporting_manager?.user_id ?? "")) payload.reporting_manager = f.reporting_manager || null;
        if (f.visibility !== (task.visibility || "private")) payload.visibility = f.visibility;
        if (f.confidential !== !!task.confidential) payload.confidential = f.confidential;
        await api.patch(`/office/tasks/${task.task_id}`, payload);
        toast.success("Task updated");
      } else if (f.template_id) {
        await api.post(`/office/tasks/from-template/${f.template_id}`, {
          assigned_to: f.assigned_to, reporting_manager: f.reporting_manager || null,
          due_date: f.due_date || null, due_time: f.due_time || null,
          visibility: f.visibility, confidential: f.confidential,
          estimated_effort_mins: f.estimated_effort_mins ? parseInt(f.estimated_effort_mins) : null,
          ack_required: f.ack_required,
          links: { customer_code: f.customer_code || null, bill_no: f.bill_no || null },
        });
        toast.success("Task created from template");
      } else {
        await api.post("/office/tasks", {
          title: f.title, description: f.description || null, department: f.department,
          category: f.category, task_type: f.task_type, assigned_to: f.assigned_to,
          reporting_manager: f.reporting_manager || null, priority: f.priority,
          due_date: f.due_date || null, due_time: f.due_time || null,
          project_id: f.project_id || null,
          visibility: f.visibility, confidential: f.confidential,
          estimated_effort_mins: f.estimated_effort_mins ? parseInt(f.estimated_effort_mins) : null,
          ack_required: f.ack_required,
          blocked_by: f.blocked_by ? f.blocked_by.split(",").map((x) => x.trim()).filter(Boolean) : [],
          links: { customer_code: f.customer_code || null, bill_no: f.bill_no || null },
          workspace: f.workspace, start_date: f.start_date || null, expected_output: f.expected_output || null,
          acceptance_criteria: f.acceptance_criteria || null, review_required: f.review_required,
          checklist: f.checklist ? f.checklist.split("\n").map((x) => x.trim()).filter(Boolean) : [],
          collaborators: f.collaborators,
        });
        toast.success("Task created");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="office-task-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase">{editing ? `Edit ${task.task_id}` : "New Task"}</DialogTitle>
          <DialogDescription className="sr-only">Create or edit a task</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Title *</Label>
            <Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="otf-title" placeholder="e.g. Prepare quotation for ABC Company" /></div>
          {!editing && dupes.length > 0 && (
            <div className="border border-signal-amber/50 bg-signal-amber/5 rounded-md p-2.5 space-y-1" data-testid="otf-duplicates">
              <p className="text-xs font-semibold text-signal-amber">Possible duplicate task</p>
              {dupes.map((d) => (
                <p key={d.task_id} className="text-xs text-muted-foreground" data-testid={`otf-dupe-${d.task_id}`}>{d.task_id} — {d.title} ({d.status.replace(/_/g, " ")}, {d.assigned_to})</p>))}
              <p className="text-[10px] text-muted-foreground">Open the existing task from My Work, or continue to create a new one.</p>
            </div>)}
          <div><Label className="text-xs">Description</Label>
            <Textarea value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="otf-description" /></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <BasicInfoSection f={f} set={set} meta={meta} editing={editing} />
            <AssignmentSection f={f} set={set} users={users} />
            <ScheduleSection f={f} set={set} meta={meta} editing={editing} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label className="text-xs">Expected output</Label><Input value={f.expected_output} onChange={(e) => set("expected_output", e.target.value)} data-testid="otf-expected-output" placeholder="e.g. Signed comparison sheet shared with MD" /></div>
            <div><Label className="text-xs">Acceptance criteria (measurable)</Label><Input value={f.acceptance_criteria} onChange={(e) => set("acceptance_criteria", e.target.value)} data-testid="otf-acceptance" placeholder="e.g. 3 quotes, landed cost, lead time" /></div>
          </div>
          {editing && (f.due_date || "") !== (task.due_date || "") && (
            <div><Label className="text-xs text-signal-amber">Reason for deadline change * (original {task.original_due_date || task.due_date || "—"})</Label>
              <Input value={f.deadline_change_reason} onChange={(e) => set("deadline_change_reason", e.target.value)} data-testid="otf-deadline-reason" /></div>)}
          {!editing && (
            <div><Label className="text-xs">Checklist (one item per line)</Label>
              <Textarea rows={2} value={f.checklist} onChange={(e) => set("checklist", e.target.value)} data-testid="otf-checklist" /></div>)}
          {!editing && (
            <div><Label className="text-xs">Collaborators / followers</Label>
              <div className="flex flex-wrap gap-1.5 mt-1 max-h-20 overflow-y-auto" data-testid="otf-collaborators">
                {users.filter((u) => u.user_id !== f.assigned_to).map((u) => (
                  <button type="button" key={u.user_id} onClick={() => set("collaborators", f.collaborators.includes(u.user_id) ? f.collaborators.filter((x) => x !== u.user_id) : [...f.collaborators, u.user_id])}
                    className={`tm-chip ${f.collaborators.includes(u.user_id) ? "tm-progress" : "tm-muted"}`}>{u.name}</button>))}
              </div></div>)}
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" className="w-4 h-4 accent-primary" checked={f.review_required} onChange={(e) => set("review_required", e.target.checked)} data-testid="otf-review-required" />
            Review required — reporting manager must approve before the task counts as completed
          </label>
          {!editing && templates.length > 0 && <TemplateSection f={f} setF={setF} templates={templates} />}
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" className="w-4 h-4 accent-primary" checked={f.ack_required} onChange={(e) => set("ack_required", e.target.checked)} data-testid="otf-ack-required" />
            Acknowledgement required — assignee must confirm "seen and understood"
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" className="w-4 h-4 accent-primary" checked={f.confidential} onChange={(e) => set("confidential", e.target.checked)} data-testid="otf-confidential" />
            Confidential — only assignee, named reporting manager, explicitly shared users and MD can view
          </label>
          {!editing && <LinksSection f={f} set={set} projects={projects} showProject={!fixedProject} />}
          <Button className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-bold" disabled={busy} onClick={submit} data-testid="otf-submit">
            {editing ? "SAVE CHANGES" : "CREATE TASK"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
